import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserPreferenceNamespace } from '~/api/userPreferences'
import { objectHookToContext } from '~/common/hookToContext'
import createDebugLogger from '~/common/logger'
import { deepDiff, frozenObj } from '~/common/objectUtils'
import { assertNotInReactContext, isWithinReactComponentOrHook } from '~/common/reactUtils'
import useDebouncedEffect from '~/hooks/useDebouncedEffect'
import { objectUrlParser, ObjectUrlParserRecord, serialisation, useUrlState } from '~/hooks/useUrlState'
import {
  AnyFilterConfig,
  BooleanFilterConfig,
  BusinessUnitFilterConfig,
  DateRangeFilterConfig,
  ListFilterConfig,
  SelectFilterConfig,
  TimeRangeFilterConfig
} from './types'

const debugProvider = createDebugLogger('FiltersProvider')

type FiltersConfig = Record<string, AnyFilterConfig>

interface FiltersProviderProps {
  config: FiltersConfig
  isConfigLoading?: boolean
  savedFilterNamespace?: UserPreferenceNamespace
}

export const useFiltersSidebar = ({ config, savedFilterNamespace, isConfigLoading = false }: FiltersProviderProps) => {
  // We rename the config to `filters` on the provided state
  const filters = config

  // Resolve the filters if they were defined without a context
  const { mergedDefaultValues, mergedObjectUrlParser, onClearObj } = useExtractThingsFromFilters(filters)

  // Keep track of values in two ways:
  // 1. The `values` object is the source of truth for the current filter value.
  //    They are updated quickly to allow widgets in the FilterSidebar to update quickly.
  // 2. The `queryValues` is a debounced copy of `values`. These values are written to the url
  //    and will be how consumers access the values. The lag prevents too many updates to the url and consumers.

  // First we initialize the queryValues, this allows it to read from the url on mount
  // to see if there are any existing values to use from the query params.
  const [values, setValues] = useUrlState(mergedDefaultValues, {
    // @ts-ignore
    parser: mergedObjectUrlParser
  })

  const [internalValues, setInternalValues] = useState<Record<string, any>>(values)

  const patchValues = useCallback(
    (patch: Partial<Record<string, any>>) => {
      debugProvider('patchValues', patch)
      setInternalValues((prevValues: Record<string, any>) => {
        const newValues = { ...prevValues, ...patch }
        // remove all keys with null or empty values
        Object.keys(newValues).forEach(key => {
          if (newValues[key] == null || newValues[key] === '') {
            delete newValues[key]
          }
        })
        debugProvider('-- newValues', newValues)
        return newValues
      })
    },
    [setInternalValues]
  )

  /**
   * An effect that will update the queryValues in the URL when the values change.
   * This is debounced to prevent too many updates to the URL.
   */
  useDebouncedEffect(
    (_: any, setValues) => {
      debugProvider('setValues', internalValues)
      setValues(internalValues)
    },
    { delay: process.env.NODE_ENV === 'test' ? 1 : 1000 },
    [internalValues],
    [setValues]
  )

  useEffect(() => {
    function handlePopState() {
      debugProvider('popstate', window.location.search)
      const parsed = mergedObjectUrlParser.parse(serialisation.parse(window.location.search))
      debugProvider('parsed', { internalValues, parsed, diff: deepDiff(internalValues, parsed) })
      setInternalValues(parsed)
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  /** Callback for clearing filter values to an empty state. */
  /*
      eslint-disable-next-line react-hooks/preserve-manual-memoization --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: `onClearObj` and `patchValues` are used inside the callback but only [isConfigLoading] is in deps.
      Possible fix approach:
        // Option 1: Add the missing dependencies:
        const clear = useCallback(() => {
          debugProvider('clear', onClearObj)
          patchValues(onClearObj)
        }, [isConfigLoading, onClearObj, patchValues])

        // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable):
        const clear = () => {
          debugProvider('clear', onClearObj)
          patchValues(onClearObj)
        }
  */
  const clear = useCallback(() => {
    debugProvider('clear', onClearObj)
    patchValues(onClearObj)
  }, [isConfigLoading])

  /** Callback for restoring filter values to a saved state. */
  const restoreSavedFilter = useCallback((savedValues: Record<string, any>) => {
    debugProvider('restoreSavedFilter', savedValues)
    setInternalValues(savedValues)
  }, [])

  return {
    filters,
    _internalValues: isConfigLoading ? (frozenObj as Record<string, any>) : internalValues,
    values: isConfigLoading ? (frozenObj as Record<string, any>) : values,
    patchValues,
    clear,
    restoreSavedFilter,
    isConfigLoading,
    savedFilterNamespace
  }
}

function useExtractThingsFromFilters(filters: FiltersProviderProps['config']) {
  return useMemo(() => {
    const mergedDefaultValues: Record<string, any> = {}
    let parsers: ObjectUrlParserRecord = {}
    let onClearObj: Record<string, any> = {}

    if (filters) {
      Object.entries(filters).forEach(([id, filter]) => {
        if (filter.urlParser) {
          parsers = { ...parsers, ...filter.urlParser }
        }
        if (filter.defaultValue) {
          mergedDefaultValues[id] = filter.defaultValue
        }
        if (filter.onClear) {
          onClearObj = { ...onClearObj, ...filter.onClear }
        }
      })
    }

    return {
      mergedDefaultValues,
      mergedObjectUrlParser: objectUrlParser(parsers),
      onClearObj
    }
  }, [filters])
}

export const [FiltersSidebarProvider, useFiltersSidebarContext] = objectHookToContext(useFiltersSidebar)

/**
 * SOME SHORTCUT HOOKS
 */

/**
 * Provides access to all filter configs as an array
 */
export const useFilters = () => useFiltersSidebarContext.select(s => s.filters)

/**
 * Provides access to the filter value patcher function. This function takes a
 * partial object of filter values and merges them into the current filter values.
 */
export const usePatchFilterValues = () => useFiltersSidebarContext.select(s => s.patchValues)

/**
 * Provides the current state of filter initialization. This is true when the filter components are still mounting and if the filter config is still loading.
 */
export const useIsFilterConfigLoading = () => useFiltersSidebarContext.select(s => s.isConfigLoading)

/**
 * Provides the savedFilterNamespace if it was defined with the filters.  You
 * can also pass this info directly into <FiltersSidebar> and it will prefer that.
 */
export const useSavedFilterNamespace = () => useFiltersSidebarContext.select(s => s.savedFilterNamespace)

/**
 * Provides a clear function that will clear all filter values.
 */
export const useClearFilters = () => useFiltersSidebarContext.select(s => s.clear)

export type FilterValues<TFilters extends Record<string, any>> = {
  [K in keyof TFilters as NonNullable<TFilters[K]> extends DateRangeFilterConfig | TimeRangeFilterConfig
    ? `${string & K}_to` | `${string & K}_from`
    : K]: NonNullable<TFilters[K]> extends BooleanFilterConfig
    ? MaybeUndefined<boolean, TFilters[K]>
    : NonNullable<TFilters[K]> extends ListFilterConfig<number> | SelectFilterConfig<number> | BusinessUnitFilterConfig
    ? MaybeUndefined<number[], TFilters[K]>
    : NonNullable<TFilters[K]> extends ListFilterConfig<string> | SelectFilterConfig<string>
    ? MaybeUndefined<string[], TFilters[K]>
    : NonNullable<TFilters[K]> extends TimeRangeFilterConfig
    ? MaybeUndefined<string | undefined, TFilters[K]>
    : NonNullable<TFilters[K]> extends DateRangeFilterConfig
    ? MaybeUndefined<Date | undefined, TFilters[K]>
    : never
}

type MaybeUndefined<TValue, TOriginal> = undefined extends TOriginal ? TValue | undefined : TValue

/**
 * Provides access to the URL search params for each filter. These params can
 * be used directly with an API call, or converted into a querystring and used to
 * update the URL.
 */
export function useFilterValues<TFilters extends Record<string, any>>() {
  return useFiltersSidebarContext.select(s => s.values) as FilterValues<TFilters>
}

/**
 * ****Should only be used by the Filter Components in the FilterSidebar.****
 *
 * Provides access to all the filter internal values as an object, where the keys are the
 * filter ids and the values are the filter values.
 */
export const useFilterInternalValues = () => useFiltersSidebarContext.select(s => s._internalValues)

/**
 * Define your list page filters statically without any dynamic transformations.
 *
 * defineFilters() must be statically defined **outside** of a React component or hook.
 * If you need to perform transformations on your filters (e.g. hide certain filters
 * based on user role, alter something based on api data), use defineFiltersWithContext() instead.
 *
 * @example
 * // First define your filters statically, **outside** of any React components or hooks
 * export const filtersDef = defineFilters({
 *   status: filter.checkboxList({ title: 'Status', ...etc })
 * })
 *
 * // Finally, use the filtersDef.Provider in your List component
 * const MyList = () => {
 *   return (
 *     <filtersDef.Provider>
 *       ...
 *       <FiltersSidebar />
 *       ...
 *     </filtersDef.Provider>
 *   )
 * }
 *
 * @agent-preferred
 * @agent-doc vndly/assets/js/components/v2/ResultsLayout/docs/Filters.mdx
 * @agent-gotcha MUST be called outside the React component.
 * @agent-gotcha Filter sidebar config (checkbox, dateRange, etc.). Pass to ListLayout via filtersDef prop.
 */
export function defineFilters<TFilters extends Record<string, any>>(
  filters: TFilters,
  savedFilterNamespace?: UserPreferenceNamespace | undefined
) {
  const isWithinReact = isWithinReactComponentOrHook()
  if (isWithinReact)
    throw new Error(
      'defineFilters() must be statically defined **outside** of a React component or hook. ' +
        'If you need to perform transformations on your filters (e.g. hide certain filters ' +
        'based on user role, alter something based on api data), use defineFiltersWithContext() instead.'
    )

  return {
    config: filters,
    isLoaded: true,
    savedFilterNamespace,
    useValues: () => useFilterValues<TFilters>()
  }
}

/**
 * TODO: (Tim) NOTE THIS IS NOT USED YET, IT WILL BE USED ON LATER LIST PAGES THAT NEED CONTEXT
 *
 * Define your list page filters with additional Context that will be used to
 * perform some dynamic transformations to your filters.
 *
 * Context could be:
 * - data from an api
 * - user's role, in order to hide certain filters
 * - data pulled from another context that will be used to transform the filters in some way
 *
 * @example
 * // First define your filters statically, **outside** of any React components or hooks
 * // (Here is a very contrived example of a filter that changes its title based on api data)
 * const myFilters = defineFiltersWithContext(({ titleFromApi }) => ({
 *   status: filter.checkboxList({ title: titleFromApi, ...etc })
 * }))
 *
 * // Then define a hook, get the dynamic data you need, and return the resolved filters
 * // Return isLoading as well to pass to FiltersProvider's `isConfigLoading` prop
 * const useMyFilters = () => {
 *   const { data, isLoading } = useApiQuery()
 *   return {
 *     filters: myFilters.resolveConfig({ titleFromApi: data.title }),
 *     isLoading
 *   }
 * }
 *
 * // Finally, use the hook in your component and pass your resolved filters to FiltersProvider
 * // as well as the isLoading prop
 * const MyComponent = () => {
 *   const { filters, isLoading } = useMyFilters()
 *   return (
 *     <FiltersProvider filters={filters} isConfigLoading={isLoading}>
 *       ...
 *     </FiltersProvider>
 *   )
 * }
 *
 * @agent-preferred
 * @agent-doc vndly/assets/js/components/v2/ResultsLayout/docs/Filters.mdx
 * @agent-gotcha MUST be called outside the React component.
 * @agent-gotcha Use over defineFilters when filter config needs runtime context (api data, user role). Returns { resolveConfig } — call inside a hook with the context, pass result to FiltersProvider along with isConfigLoading.
 */
export function defineFiltersWithContext<TContext, TFilters extends Record<string, any>>(
  filtersCb: (ctx: TContext) => {
    config: TFilters
    isLoaded: boolean
    savedFilterNamespace?: UserPreferenceNamespace | undefined
  }
) {
  assertNotInReactContext()

  const useValues = useFilterValues<TFilters>

  return {
    resolveConfig: (ctx: TContext) => {
      const filtersDef = filtersCb(ctx)
      return {
        config: filtersDef.config,
        isLoaded: filtersDef.isLoaded,
        savedFilterNamespace: filtersDef.savedFilterNamespace ?? undefined,
        useValues
      }
    },
    useValues
  }
}
